import React, { ChangeEvent } from "react";
import { Autocomplete } from "@mui/material";
import { CircularProgress, styled, FormControl, TextField, Box } from "@mui/material";

import { getIn } from "formik";
import { InputMessage, ContextualHelp } from "../index";

const StyledFormControl = styled(FormControl)({
  width: "100%",
});

interface IProps {
  field: {
    name: string;
    value: string;
  };
  form: {
    errors: {
      optionalObject: Record<string, string>;
    };
    setFieldValue: (path: string, values: string) => void;
    values: Record<string, string>;
  };
  label: string;
  placeholder: string;
  contextualHelpText: string;
  contextualHelpProps: Record<string, unknown>;
  options: { value: string; label: string }[];
  onChangeTextField: (event: ChangeEvent<HTMLInputElement>) => void;
  onChange: (event: React.SyntheticEvent, value: unknown) => void;
  extractValue?: (option: { value: string; label: string }) => string;
  extractLabel?: (option: { value: string; label: string }) => string;
  getOptionSelected?: (
    option: { value: string; label: string },
    value: { value: string; label: string }
  ) => boolean;
  autocompleteProps?: {
    disableClearable: true;
  };
  loading?: boolean;
}
const FormAutocomplete = ({
  field,
  form,
  label,
  placeholder,
  contextualHelpText,
  contextualHelpProps,
  options = [],
  autocompleteProps,
  onChangeTextField,
  onChange,
  extractValue = (option) => option.value,
  extractLabel = (option) => option.label,
  getOptionSelected = (option, value) => extractLabel(option) === value?.value,
  loading = false,
  ...rest
}: IProps): React.ReactElement => {
  const errorMessage = getIn(form.errors, field.name);
  // const initialValue = getIn(form.values, field.name);
  const handleInput = (
    event: ChangeEvent<Record<string, unknown>>,
    value: { value?: string }
  ): void => {
    form.setFieldValue(field.name, value?.value || "");
  };

  const renderValue = options.find((option) => field.value === extractValue(option));

  return (
    <StyledFormControl>
      <ContextualHelp title={contextualHelpText} {...contextualHelpProps}>
        <Autocomplete
          {...rest}
          value={renderValue}
          options={options}
          onChange={onChange || handleInput}
          getOptionLabel={extractLabel}
          isOptionEqualToValue={getOptionSelected}
          loading={loading}
          renderInput={(params) => (
            <TextField
              {...params}
              onChange={onChangeTextField}
              label={label}
              placeholder={placeholder}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <React.Fragment>
                    {loading ? <CircularProgress color="inherit" size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </React.Fragment>
                ),
              }}
            />
          )}
        />
      </ContextualHelp>
      <Box position="absolute" left={0} top="55px">
        {errorMessage && <InputMessage error={errorMessage} />}
      </Box>
    </StyledFormControl>
  );
};

export default FormAutocomplete;
